import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, InfoNote } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const STATE_CLS = {
  Unanswered: "bg-slate-100 text-slate-500 border-slate-300",
  "Partially Answered": "bg-amber-50 text-amber-800 border-amber-300",
  Confirmed: "bg-emerald-50 text-emerald-700 border-emerald-300",
  "Requires External Vendor": "bg-sky-50 text-sky-700 border-sky-300",
  "Requires Compliance Review": "bg-rose-50 text-rose-700 border-rose-300",
};

export default function DomainQuestions() {
  const { roleCode, persona } = useApp();
  const [data, setData] = useState(null);
  const [drafts, setDrafts] = useState({});

  const load = useCallback(() => api.domainQuestions().then(setData).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div className="text-sm text-slate-500">Loading domain questions…</div>;

  const setDraft = (id, patch) => setDrafts((d) => ({ ...d, [id]: { ...d[id], ...patch } }));
  const save = async (q) => {
    const dr = drafts[q.id] || {};
    await api.answerDomainQuestion({ question_id: q.id, answer: dr.answer || "", status: dr.status || "Partially Answered", role_code: roleCode, captured_by: persona?.name });
    toast.success("Answer captured"); load();
  };

  return (
    <div>
      <PageHeader testid="domain-questions-header" title="Questions We Need Answered Before Live Pilot"
        subtitle="Turn tomorrow's session into structured requirements. Capture answers and mark their state." />
      <InfoNote className="mb-4">Nothing here is assumed. Each item stays “Unanswered” until a person provides a validated answer.</InfoNote>

      <div className="space-y-4">
        {data.groups.map((g) => (
          <Card key={g.group} className="p-4" data-testid={`dq-group-${g.group.split(" ")[0].toLowerCase()}`}>
            <div className="mb-3 font-display text-sm font-bold text-slate-900">{g.group}</div>
            <div className="space-y-2">
              {g.questions.map((q) => {
                const captured = q.captured;
                const dr = drafts[q.id] || {};
                return (
                  <div key={q.id} className="rounded-md border border-slate-200 p-3" data-testid={`dq-${q.id}`}>
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm text-slate-800">{q.text}</span>
                      <span className={`rounded-md border px-2 py-0.5 text-[10px] font-semibold ${STATE_CLS[captured?.status || "Unanswered"]}`}>{captured?.status || "Unanswered"}</span>
                    </div>
                    {captured?.answer && <div className="mt-1 text-xs text-teal-800">Answer: {captured.answer} <span className="text-[10px] text-slate-400">— {captured.captured_by}</span></div>}
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <Input className="h-8 max-w-md text-xs" data-testid={`dq-answer-${q.id}`} placeholder="Capture answer…" value={dr.answer || ""} onChange={(e) => setDraft(q.id, { answer: e.target.value })} />
                      <Select value={dr.status || "Partially Answered"} onValueChange={(v) => setDraft(q.id, { status: v })}>
                        <SelectTrigger className="h-8 w-52 text-xs" data-testid={`dq-status-${q.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{data.states.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                      <Button size="sm" variant="outline" data-testid={`dq-save-${q.id}`} onClick={() => save(q)}>Save</Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
